import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { convertDataDictionaryToMarkdown, convertDeltaTableDictToMarkdown } from '@app/utils';
import { SummaryType } from '@common/enums/SummaryType';
import { IDeltaTable } from '@common/models/Document/IDeltaTable';
import { IDocument } from '@common/models/Document/IDocument';
import jsPDF from 'jspdf';
import { DialogService } from 'primeng/dynamicdialog';

@Component({
    selector: 'app-document-summary-dialog',
    templateUrl: './document-summary-dialog.component.html',
    styleUrls: ['./document-summary-dialog.component.scss'],
    providers: [DialogService]
})
export class DocumentSummaryDialogComponent implements OnChanges {
    @ViewChild('documentText', { static: false }) documentText: ElementRef;
    @Input() sidebarVisible: boolean;
    @Input() summaryData: string;
    @Input() documentModel: IDocument;
    @Input() longSummaryType: string;
    @Input() header: string;
    @Input() dialogType: string;
    @Input() documentName: string;
    @Input() deltaTable: IDeltaTable;
    iscopyingSummary: boolean = false;
    downloadSummary: boolean = false;
    summaryTypeCIM: SummaryType = SummaryType.CIM;
    @Output() emitSidebarUpdate = new EventEmitter<boolean>();
    summaryDataSplit: string[];
    excelType = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
    ];

    constructor(public dialogService: DialogService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['summaryData']) {
            this.summaryDataSplit =
                this.longSummaryType == SummaryType.CIM
                    ? this.summaryData.split(/\n{4,}/)
                    : [this.summaryData];
            if (this.header === 'Summary') {
                this.documentModel = null;
                this.deltaTable = null;
            }
        }
        if (
            changes['documentModel'] ||
            changes['sidebarVisible']?.currentValue === true
        ) {
            if (this.documentModel?.dataDictionary) {
                this.summaryData = convertDataDictionaryToMarkdown(
                    this.documentModel
                );
            }
        }
        if (
            (changes['deltaTable'] ||
                changes['sidebarVisible']?.currentValue === true) &&
            this.header !== 'Summary'
        ) {
            if (this.deltaTable?.dataDictionary) {
                this.summaryData = convertDeltaTableDictToMarkdown(
                    this.deltaTable?.dataDictionary,
                    this.deltaTable?.tableName
                );
            }
        }
    }

    async copyText(): Promise<void> {
        // Visual feedback
        this.iscopyingSummary = true;
        setTimeout(() => {
            this.iscopyingSummary = false;
        }, 1000);
        
        const textToCopy = this.documentText?.nativeElement?.innerText ?? '';

        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
            await navigator.clipboard.writeText(textToCopy).catch(() => {
                // Fallback if Clipboard API fails (e.g., permissions/insecure context)
                this.selectTextForManualCopy();
            });
        } else {
            // Fallback if Clipboard API is unavailable
            this.selectTextForManualCopy();
        }

    }

    private selectTextForManualCopy(): void {
        const el = this.documentText?.nativeElement;
        if (!el) return;
        const selection = window.getSelection?.();
        if (!selection) return;
        selection.removeAllRanges();
        const range = document.createRange();
        range.selectNodeContents(el);
        selection.addRange(range);
    }

    downloadPdfFile() {
        this.downloadSummary = true;
        const doc = new jsPDF('p', 'mm', 'a4');
        const anchor = this.documentText.nativeElement.innerText;
        const fontSize = 11;
        let currentY = 20;
        const lineHeight = 6;

        if (anchor) {
            doc.setFont('IBM Plex Sans', 'bold');
            doc.setFontSize(14);
            doc.text(this.documentName, 10, currentY);
            doc.setFontSize(fontSize);
            currentY = currentY + 10;
            const textlines =
                this.longSummaryType == SummaryType.CIM
                    ? this.summaryData.split(/\n{4,}/)
                    : [this.summaryData];
            textlines.forEach((section) => {
                section.split('\n\n').forEach((line, index) => {
                    if (
                        index == 0 &&
                        section.split('\n\n').length > 0 &&
                        this.longSummaryType === this.summaryTypeCIM
                    ) {
                        currentY = currentY + 10;
                        doc.setFont('IBM Plex Sans', 'bold');
                    } else {
                        currentY = currentY + 2;
                        doc.setFont('IBM Plex Sans', 'normal');
                    }
                    const splitLines = doc.splitTextToSize(line, 180);
                    splitLines.forEach((subLine) => {
                        doc.text(subLine, 10, currentY);
                        currentY += lineHeight;
                        if (currentY > doc.internal.pageSize.getHeight() - 20) {
                            doc.addPage();
                            currentY = 20;
                        }
                    });
                });
            });

            doc.save(this.documentName);
        }

        setTimeout(() => {
            this.downloadSummary = false;
        }, 1000);
    }

    onCloseSidebar() {
        this.emitSidebarUpdate.emit(false);
    }
}
